import { Button, Col, Divider, Form as AntForm, Input, Row } from "antd";
import {
  DATE_FORMAT,
  DialogContent,
  InteractiveBlock,
  Keyboard,
  UserProfile,
} from "api/dialog";
import { DialogBlockProps, DialogBox, DialogHistoryItem } from "components/DialogBox";
import AutoInsurance from "resources/AutoInsurance.svg";
import BuildInsurance from "resources/BuildInsurance.svg";
import HealthInsurance from "resources/HealthInsurance.svg";
import TurInsurance from "resources/TurInsurance.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  assert,
  contextReplace,
  emitEvent,
  getCartItemFromCart,
  numberFormatter,
  omit,
  promisedComponent,
  useMobileQuery,
} from "utils/utils";
import { ok, err, emptyOk, unwrapOk, type Result } from "utils/types";
import {
  apiCall,
  bitrixLeadFromDeal,
  frontendAjaxCall,
  getAuthedUser,
  JSON_CONTENT_TYPE,
  PRIVACY_POLICY_URL,
  productLink,
  type RequestError,
} from "api/hooks";
import Auth from "./components/Auth";
import ProductAddBadge from "./components/ProductAddBadge";
import { usePromise, usePromiseExt } from "utils/usePromise";
import { TitledInputForm } from "./components/InputForm";
import { useForm } from "antd/lib/form/Form";
import { Rule } from "antd/lib/form";
import { evalAccessStr, evalExprStr } from "utils/evaluator";
import { Buttons } from "./components/Buttons";
import { DialogBoxFooter, DIALOG_BOX_PORTAL_ID } from "components/Scenario";
import moment from "moment";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { Loader } from "components/Loader";
import { FRONTEND_DOMAIN, HTTP_POST_REQUEST_DOMAIN, NUMBER_FORMAT_LOCALE } from "config";
import i18n from "i18next";
import { getWidgetState } from "utils/useStorage";
import styles from "./Interactive.module.scss";
import classNames from "classnames";
import { useLanguage } from "utils/useLanguage";
import { Portal } from "components/Portal";
import { suspendedLazy } from "utils/suspended";
import Flex from "components/Flex";
import PaymentButtons from "./PaymentButtons/PaymentButtons";

const ScenarioFormItem = suspendedLazy(
  () =>
    import("./components/ScenarioFormItem").then(
      ({ ScenarioFormItem }) => ScenarioFormItem
    ),
  Loader({ mode: "keyboard" })
);

export const CATEGORY_ICONS = {
  auto: AutoInsurance,
  build: BuildInsurance,
  health: HealthInsurance,
  tourism: TurInsurance,
} as const;

export const PromocodeBranch = "PromocodeBranch";

const searchInterval =
  <T, U = { branch: string }>(compare: (a1: T, a2: T) => boolean) =>
  (b: T, a?: ({ nextBound: T } & U)[]): U | undefined => {
    if (!a) return;

    for (const item of a) {
      if (compare(b, item.nextBound)) return item;
    }
  };

export type InteractiveBlockComponent = (props: DialogBlockProps) => JSX.Element;

const Interactive = (
  block: (InteractiveBlock | DialogContent) & { title?: string }
): InteractiveBlockComponent => {
  const getSender =
    (path: string, sender: DialogBlockProps["onChange"], block: InteractiveBlock) =>
    ({ text, ...x }: { text: string; value: unknown; next?: string; name?: string }) =>
      sender?.({ path, block, ...x }, { dialogBlockType: "user", type: "text", text });

  // TODO: make "changing content box" an command from blocks
  // TODO: refactor large block types into separate components, similar to auth
  // TODO: refactor usePromise usage in some block types so that no outer keyboard rerenders needed
  switch (block.type) {
    case "text":
    case "photo":
    case "audio":
    case "video":
    case "file":
      return function Content({ onChange }) {
        useEffect(() => {
          onChange?.({ pushStack: false }, {
            dialogBlockType: "bot",
            ...block,
          } as DialogHistoryItem);
        });
        return <></>;
      };
    case "branch":
      return function Branch({ onChange, data, context }) {
        useEffect(() => {
          const _eval = (rule: string) =>
            evalExprStr(rule, {
              ...data,
              data,
              ctx: context,
            });
          const next = block.matchers.find(({ rule }) => _eval(rule))?.next;

          onChange?.({ pushStack: false, next });
        });
        return <></>;
      };
    case "customPay":
      return ({ context }) => (
        <PaymentButtons
          hash={context.orderHash}
          buttons={context.response.data.paymentTypes}
        />
      );
    case "button":
      // TODO: remove when scenarios updated
      if (block.blockName === "ctx.payment") {
        return ({ context }) => (
          <PaymentButtons
            hash={context.orderHash}
            buttons={context.response.data.paymentTypes}
          />
        );
      }

      return ({ mobile, className, onChange, context, data, contrastGrayscale }) => (
        <Buttons
          mobile={mobile}
          className={className}
          title={block.title}
          contrastGrayscale={contrastGrayscale}
          items={block.items.map(({ name, value: itemValue, link, next: _next }) => ({
            name: contextReplace(name, {
              ...data,
              data,
              ctx: context,
            }),
            onClick: () => {
              let value: unknown;
              if (block.dynamicValues)
                value = itemValue
                  ? evalExprStr(itemValue, {
                      ...data,
                      data,
                      ctx: context,
                    }) ?? name
                  : name;
              else value = itemValue ?? name;

              if (link && typeof value === "string") {
                window.top?.location.assign(value);
              }

              const send = getSender(block.blockName, onChange, block);
              const next =
                _next ??
                block.branching?.next?.find(
                  ({ value: branchValue }) => branchValue === value
                )?.branch ??
                block.branching?.default;

              send({ text: name, value, next, name });
            },
          }))}
        />
      );
    case "location":
      return ({ className, onChange, context, mobile, data, contrastGrayscale }) => {
        const item = block;
        const evalContext = { ...data, data, ctx: context };
        const rules = [
          {
            validator: async (_: any, value: unknown) => {
              if (!("required" in item) || !item.required) return;
              if (value === undefined || value === null)
                throw new Error(i18n.t("input.required"));
            },
          },
        ];

        let initialValue: any = item.defaultValue
          ? evalExprStr(item.defaultValue, evalContext)
          : item.resetOnEnter
          ? undefined
          : item.blockName && evalExprStr(item.blockName, evalContext);

        return (
          <TitledInputForm
            kind="large"
            title={block.title}
            contrastGrayscale={contrastGrayscale}
            initialValues={initialValue}
            mobile={mobile}
            rules={rules}
            onFinish={(val) => {
              onChange?.({
                path: block.blockName,
                block,
                value: val,
                name: block.blockName,
                pushStack: true,
                progressScenario: true,
              });
            }}
          >
            <ScenarioFormItem
              item={block}
              context={evalContext}
              className={classNames(className, styles.input)}
            />
          </TitledInputForm>
        );
      };
    case "date":
    case "fileInput":
    case "dropdown":
    case "input":
    case "numberInput":
    case "phone":
    case "slider":
      return ({ className, onChange, context, mobile, data, contrastGrayscale }) => {
        const item = block;
        const evalContext = { ...data, data, ctx: context };
        const rules = [
          {
            validator: async (_: any, value: unknown) => {
              if (!("required" in item) || !item.required) return;
              if (item.type === "input" && !value)
                throw new Error(i18n.t("input.required"));
              if (value === undefined || value === null)
                throw new Error(i18n.t("input.required"));
            },
          },
          "validation" in item && item.validation
            ? (item.validation as unknown as Rule[])
            : [],
          item.type === "phone"
            ? ([
                {
                  validator: async (_: any, value: string) => {
                    value = value.startsWith("+") ? value : "+" + value;
                    if (isPossiblePhoneNumber(value)) return;
                    else throw new Error(i18n.t("input.impossiblePhoneNumber"));
                  },
                },
              ] as unknown as Rule[])
            : [],
        ]
          .flat()
          .map((rule) =>
            "pattern" in rule && rule.pattern
              ? ({
                  ...rule,
                  pattern: new RegExp("^" + new RegExp(rule.pattern).source + "$"),
                } as Rule)
              : rule
          );
        let initialValue: any = item.defaultValue
          ? evalExprStr(item.defaultValue, evalContext)
          : item.resetOnEnter
          ? undefined
          : item.blockName && evalExprStr(item.blockName, evalContext);

        if (item.type === "date" && initialValue !== undefined)
          initialValue = {
            value: moment
              .utc(new Date(initialValue as string | number))
              .format("YYYY-MM-DD"),
          };
        if (item.type === "date" && initialValue !== undefined)
          initialValue = ((item.disabledDate &&
            evalExprStr(item.disabledDate, {
              ...evalContext,
              selected: initialValue.value,
            })) as boolean)
            ? undefined
            : initialValue;
        if (item.type === "slider" && initialValue !== undefined)
          initialValue = +initialValue;
        if (item.type === "dropdown" && initialValue !== undefined)
          initialValue = { value: initialValue };

        return (
          <TitledInputForm
            title={block.title}
            contrastGrayscale={contrastGrayscale}
            initialValues={initialValue}
            mobile={mobile}
            rules={rules}
            borderless
            required={block.required}
            keyboardPosition={block.keyboard?.position}
            keyboard={
              block.keyboard
                ? block.keyboard.items.map(({ name, value: itemValue, link }) => ({
                    name,
                    onClick: () => {
                      const _block = block.keyboard as Keyboard;

                      let value: unknown;
                      if (_block.dynamicValues)
                        value = itemValue
                          ? evalExprStr(itemValue, evalContext) ?? name
                          : name;
                      else value = itemValue ?? name;
                      if (link && typeof value === "string") {
                        window.top?.location.assign(value);
                      }

                      const send = getSender(_block.blockName, onChange, block);
                      const next =
                        _block.branching?.next?.find(
                          ({ value: branchValue }) => branchValue === value
                        )?.branch ?? _block.branching?.default;
                      send({ text: name, value, next, name });
                    },
                  }))
                : undefined
            }
            onFinish={(val) => {
              const send = getSender(block.blockName, onChange, block);
              if (block.type === "dropdown") {
                const { value, name } = val;
                const next =
                  block.branching?.next?.find(
                    ({ value: branchValue }) => branchValue === value.toString()
                  )?.branch ?? block.branching?.default;
                return send({ text: name, value, next, name });
              }
              if (block.type === "input") {
                const next =
                  block.branching?.next?.find(
                    ({ value: branchValue }) => branchValue === val
                  )?.branch ?? block.branching?.default;

                return send({ text: val, value: val, next, name: val });
              }
              if (block.type === "numberInput") {
                const next =
                  block.branching?.next?.find(
                    ({ value: branchValue }) => branchValue === val
                  )?.branch ?? block.branching?.default;
                const formatter = numberFormatter(NUMBER_FORMAT_LOCALE);
                const formatted = formatter(val);

                return send({
                  text: formatted,
                  value: val,
                  next,
                  name: formatted,
                });
              }
              if (block.type === "phone") {
                return send({ text: val, value: val, name: val });
              }
              if (block.type === "date") {
                const { value, name } = val;
                const next =
                  searchInterval<Date>((d1, d2) => moment(d1).isAfter(d2))(
                    new Date(value),
                    block.branching?.next
                  )?.branch ?? block.branching?.default;
                return send({ text: name, value, next, name });
              }
              if (block.type === "slider") {
                const next =
                  searchInterval<number>((a, b) => a < b)(val, block.branching?.next)
                    ?.branch ?? block.branching?.default;
                return send({
                  text: val.toString(),
                  name: val.toString(),
                  value: val,
                  next,
                });
              }
            }}
          >
            <ScenarioFormItem
              item={block}
              context={evalContext}
              className={classNames(className, styles.input, {
                [styles.select]: block.type === "dropdown",
              })}
            />
          </TitledInputForm>
        );
      };
    case "suggestions":
      return function Suggestions({ onChange, context }) {
        const relevant = context.relevant as any[];
        const lang = useLanguage();

        useEffect(() => {
          const x = setTimeout(() => {
            if (block.skipable && relevant.length === 0) onChange?.({ pushStack: false });
          });
          return () => clearTimeout(x);
        });
        if (relevant.length === 0) return <></>;
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: 16,
              width: "100%",
              background: "white",
              padding: "12px 24px 30px 16px",
              borderRadius: "1px 12px 12px 12px",
            }}
          >
            <div>{block.text}</div>
            <Row gutter={[12, 12]}>
              {relevant.map(({ name, description, slug }, i, arr) => {
                return (
                  <Col
                    span={arr.length % 2 === 1 && i === arr.length - 1 ? 24 : 12}
                    key={i}
                  >
                    <ProductAddBadge
                      name={name}
                      hint={description}
                      onClick={() => {
                        window.top?.location.assign(productLink(slug, lang));
                        // const updated = new URLSearchParams(
                        //   window.location.search
                        // );
                        // updated.set("productId", id);
                        // window.location.search = updated.toString();
                      }}
                    />
                  </Col>
                );
              })}
            </Row>
            {block.skipable && (
              <Buttons
                items={[{ name: i18n.t("widget.skip"), onClick: () => onChange?.({}) }]}
              />
            )}
          </div>
        );
      };
    case "sendEmail":
      return function SendEmail({ onChange, context }) {
        const authed = context.authed as any;
        const promised = usePromise(async () => {
          if (!authed.email) {
            onChange?.(
              { pushStack: false, progressScenario: false },
              {
                dialogBlockType: "bot",
                text: i18n.t("sendEmail.noEmail"),
                type: "text",
              }
            );
            return ok("noEmail");
          }
          await frontendAjaxCall(
            "/web/payment/order-contract-share/" + context.orderHash,
            { method: "POST" }
          );
          onChange?.({});
          return emptyOk();
        });
        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgShort",
            title: i18n.t("preloader.sendEmail"),
          }),
          resolved: function Resolved({ res }) {
            if (res === "noEmail")
              return (
                <TitledInputForm
                  borderless
                  required
                  rules={[
                    {
                      pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/,
                      message: i18n.t("input.invalidEmail"),
                    },
                  ]}
                  onFinish={async (email) => {
                    onChange?.(
                      { pushStack: false, progressScenario: false },
                      {
                        dialogBlockType: "user",
                        text: email,
                        type: "text",
                      }
                    );

                    await frontendAjaxCall(
                      "/web/payment/order-contract-share/" + context.orderHash,
                      {
                        method: "POST",
                        body: { email },
                      }
                    );
                    onChange?.({});
                  }}
                >
                  <Input placeholder={i18n.t("placeholder.email")} />
                </TitledInputForm>
              );
            return <></>;
          },
        });
      };
    case "signUpToRepresentativeOffice":
      return function SignUpToRepresentativeOffice({ onChange }) {
        useEffect(() => {
          const x = setTimeout(() => {
            onChange?.({ pushStack: false });
          });
          return () => clearTimeout(x);
        });
        return <></>;
      };
    case "paymentLink":
      return function Payment({ onChange, context, data }) {
        const recurrent = block.recurrent
          ? evalExprStr(block.recurrent, {
              ...data,
              data,
              ctx: context,
            }) ?? context.recurrent
          : context.recurrent;
        const promised = usePromise(async () => {
          const {
            data: { url: link },
          } = await frontendAjaxCall("/web/payment/pay", {
            body: {
              hash: context.orderHash as string,
              successUrl: "assets/app/interactive-app.html?path=payment&status=success",
              errorUrl: "assets/app/interactive-app.html?path=payment&status=error",
              recurrent,
            },
          });
          return ok(link);
        });

        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgLong",
            title: i18n.t("preloader.paymentLink"),
          }),
          resolved: function Resolved({ res: link }) {
            useEffect(() => {
              // const search = new URLSearchParams(window.top?.location.search);
              // const cartItemId = search.get("cartItemId") ?? "";
              // const contractTotal =
              //   context.response.data.contracts[cartItemId].payment ?? 0;
              // const total = context.response.data.order?.payment ?? 0;
              onChange?.(
                { path: block.path, value: link, pushStack: false }
                // {
                //   dialogBlockType: "bot",
                //   type: "text",
                //   text: i18n.t("payment.contractPrice", {
                //     // discountText,
                //     total: contractTotal,
                //     // cartTotal,
                //     // promocode,
                //   }),
                // },
                // {
                //   dialogBlockType: "bot",
                //   type: "text",
                //   text: i18n.t("payment.totalPrice", {
                //     // discountText,
                //     total,
                //     // cartTotal,
                //     // promocode,
                //   }),
                // }
              );
            });
            return <></>;
          },
        });
      };
    case "awaitPayment":
      return function Payment({ onChange, context }) {
        const promised = usePromise(async (updateTitle) => {
          // updateTitle(i18n.t("preloader.awaitPayment"));

          // {
          // await frontendAjaxCall(
          //   "/web/payment/order-status/" + context.documentId,
          //   { method: "GET" }
          // );
          // const orderResPromise = nextSocketEventPrivate(
          //   ".order.status",
          //   "order-status-" + context.documentId
          // );

          // updateTitle(i18n.t("preloader.awaitPaymentBankSuccess"));
          // const orderRes: any = await orderResPromise;

          // }

          // const paymentRes = await nextWindowMessage("payment").then(
          //   ({ searchParams }) => new URLSearchParams(searchParams)
          // );

          // if (
          //   paymentRes.get("errorCode") !== null &&
          //   paymentRes.get("acquiring") === "easy_pay"
          // )
          //   return err({
          //     message: i18n.t("awaitPayment.error." + paymentRes.get("errorCode")),
          //   });

          // updateTitle(i18n.t("preloader.awaitPaymentBankSuccess"));
          // try {
          // const nextEvent = nextSocketEvent<any>(
          //   "/order-status/" + context.orderHash
          // ).then((msg) => some(JSON.parse(msg.data)));
          // const nextEvent = pollEvent(async () => {
          //   const x = await frontendAjaxCall(
          //     "/api/v1/payment/order-status/" + context.orderHash,
          //     { method: "GET" }
          //   );
          //   await wait(1000);
          //   if (x.order_status === 7) return { done: false, event: none() };
          //   return { done: true, event: some(x) };
          // });
          // await frontendAjaxCall("/web/payment/order-status/" + context.orderHash, {
          //   method: "GET",
          // });
          // const orderRes = await Promise.race([
          //   nextEvent,
          //   wait(PAYMENT_TIMEOUT).then(none),
          // ]);
          // if (orderRes.type === "none")
          //   throw new Error(i18n.t("awaitPayment.error.timeout"));

          // const { order_status: orderStatus, order_status_text: orderStatusText } =
          //   orderRes.value;

          // // console.log("ws message", orderRes.value);
          // if (orderStatus !== OrderStatus.Paid) throw new Error(orderStatusText);
          // updateTitle(i18n.t("preloader.awaitPaymentReceipt"));
          // const receipt = await frontendAjaxCall(
          //   "/web/payment/order-receipt/" + context.orderHash,
          //   {
          //     method: "GET",
          //   }
          // );
          // const link = receipt.data.link;

          //   onChange?.(
          //     { pushStack: false },
          //     {
          //       dialogBlockType: "bot",
          //       type: "text",
          //       text: i18n.t("awaitPayment.receipt"),
          //     },
          //     {
          //       dialogBlockType: "bot",
          //       type: "file",
          //       name: i18n.t("awaitPayment.receiptFileName"),
          //       link,
          //     }
          //   );
          // } catch (e: any) {
          //   onChange?.({ pushStack: false, next: "back" });
          //   onChange?.(
          //     { pushStack: false, progressScenario: false },
          //     {
          //       dialogBlockType: "bot",
          //       type: "text",
          //       text: e.message,
          //     }
          //   );
          // }

          // just skip that block
          onChange?.({ pushStack: false, progressScenario: false });
          return emptyOk();
        });
        return promisedComponent(promised, {
          resolved: function Resolved() {
            return <></>;
          },
        });
      };
    case "setValue":
      return function SetValue({ onChange, context, data }) {
        useEffect(() => {
          const x = setTimeout(() => {
            onChange?.({
              path: block.path,
              value: evalExprStr(block.value, {
                ...data,
                data,
                ctx: context,
              }),
              pushStack: false,
            });
          });
          return () => clearTimeout(x);
        });
        return <></>;
      };
    case "pushValue":
      return function SetValue({ onChange, context, data }) {
        useEffect(() => {
          const arr = evalExprStr(block.path, {
            ...data,
            data,
            ctx: context,
          });
          const x = setTimeout(() => {
            onChange?.({
              path: block.path,
              value: [
                ...(Array.isArray(arr) ? arr : []),
                evalExprStr(block.value, {
                  ...data,
                  data,
                  ctx: context,
                }),
              ],
              pushStack: false,
            });
          });
          return () => clearTimeout(x);
        });
        return <></>;
      };
    case "addToCart":
      return function Payment({ onChange, context, data }) {
        const product = block.product ?? context.productId;
        const total = block.total
          ? evalExprStr(block.total, {
              ...data,
              data,
              ctx: context,
            }) ?? data.total
          : data.total;
        const itemInfo =
          block.itemInfo?.map(({ name, value }) => ({
            name,
            value: evalExprStr(value, {
              ...data,
              data,
              ctx: context,
            }),
          })) ?? [];
        const _data = block.data
          ? evalExprStr(block.data, {
              ...data,
              data,
              ctx: context,
            }) ?? data
          : data;

        const promised = usePromise(async () => {
          return ok(
            await frontendAjaxCall(`/web/cart/add/${product}`, {
              method: "POST",
              body: {
                total: total as string,
                widget_state: getWidgetState(),
                calculate_data: _data,
                item_info: itemInfo,
              },
              headers: { "Content-Type": JSON_CONTENT_TYPE },
            })
          );
        });
        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgShort",
            title: i18n.t("preloader.addToCart"),
          }),
          resolved: function Resolved({ res }) {
            useEffect(() => {
              if (!res) {
                onChange?.({ pushStack: false });
                return;
              }
              const {
                data: { new_contract_id },
              } = res;
              const search = new URLSearchParams(window.top?.location.search);
              search.set("cartItemId", new_contract_id);
              if (search.has("state") && search.get("state") === "change")
                search.delete("state");
              window.top?.history.replaceState(
                "",
                "",
                window.top?.location.pathname + "?" + search.toString()
              );

              emitEvent(
                "add-to-cart-success",
                { contractId: new_contract_id },
                window.top?.document.body
              );

              onChange?.({
                value: new_contract_id,
                path: "ctx.cartItemId",
                pushStack: false,
              });
            });
            return <></>;
          },
        });
      };
    case "ppDocument":
      return function Payment({ onChange }) {
        const promised = usePromise(async () => {
          const search = new URLSearchParams(window.top?.location.search);
          const cartItemId = search.get("cartItemId");
          if (!cartItemId) return err({ message: "No cart item id" });
          const {
            data: { contracts: cartContracts },
          } = await frontendAjaxCall("/web/cart", { method: "GET" });

          // for (const {
          //   widget_state: {
          //     context: { contractDataSet, cartItemId, productId },
          //   },
          // } of cartItems) {
          //   if (!contractDataSet) {
          //     const topSearchParams = new URLSearchParams(
          //       window.top?.location.search ?? ""
          //     );
          //     const searchParams = new URLSearchParams(
          //       window.location.search ?? ""
          //     );
          //     topSearchParams.set("cartItemId", cartItemId.toString());
          //     searchParams.set("cartItemId", cartItemId.toString());
          //     searchParams.set("productId", productId);
          //     topSearchParams.set("isNextProduct", "true");
          //     searchParams.set("isNextProduct", "true");
          //     window.top?.history.replaceState(
          //       "",
          //       "",
          //       window.top?.location.pathname + "?" + topSearchParams.toString()
          //     );
          //     window.history.replaceState(
          //       "",
          //       "",
          //       window.location.pathname + "?" + searchParams.toString()
          //     );
          //     onChange?.({ resetProduct: true });

          //     return emptyOk();
          //   }
          // }

          const item = getCartItemFromCart(cartContracts, cartItemId);
          if (!item)
            return err({
              message: "No such item in cart, " + cartItemId,
            });

          const resDoc = await frontendAjaxCall(
            "/web/calculators/integrity/save-insurance/" + item.order_hash,
            {
              method: "POST",
              headers: { "Content-Type": JSON_CONTENT_TYPE },
            }
          );

          const {
            data: { contracts },
          } = resDoc;

          emitEvent(
            "after-save",
            { contractId: cartItemId, contracts, cartContracts },
            window.top?.document.body
          );

          onChange?.(
            {
              path: "ctx.orderHash",
              value: item.order_hash,
              pushStack: false,
              progressScenario: false,
            },
            ...Object.entries(
              contracts as {
                [id in string]: { pp_document: string; pp_document_name: string };
              }
            ).map(([id, { pp_document, pp_document_name }]) => ({
              dialogBlockType: "bot" as const,
              type: "file" as const,
              link: pp_document,
              name: `${pp_document_name}.pdf`,
            }))
          );

          onChange?.({
            value: cartItemId,
            path: "ctx.cartItemId",
            pushStack: false,
            progressScenario: false,
          });
          onChange?.({
            value: resDoc,
            path: block.path ?? "ctx.response",
            pushStack: false,
          });

          return emptyOk();
        });
        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgLong",
            title: i18n.t("preloader.ppDocument"),
          }),
          resolved: function Resolved() {
            return <></>;
          },
        });
      };
    case "setContractData":
      return function Payment({ onChange, context, data }) {
        const promised = usePromise(async () => {
          const contractData = block.data
            ? evalExprStr(block.data, {
                ...data,
                data,
                ctx: context,
              }) ?? data
            : data;
          const search = new URLSearchParams(window.top?.location.search);
          const cartItemId = search.get("cartItemId");
          if (cartItemId) {
            onChange?.({
              path: "ctx.contractDataSet",
              value: true,
              progressScenario: false,
              pushStack: false,
            });
            await frontendAjaxCall("/web/cart/update/" + cartItemId, {
              body: { contract_data: contractData },
              headers: { "Content-Type": JSON_CONTENT_TYPE },
            });
          }

          return emptyOk();
        });
        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgLong",
            title: i18n.t("preloader.setContractData"),
          }),
          resolved: function Resolved() {
            useEffect(() => {
              onChange?.({ pushStack: false });
            });
            return <></>;
          },
        });
      };
    case "bitrixTrigger":
      return function BitrixTrigger({ onChange, context }) {
        const promised = usePromise(async () => {
          await bitrixLeadFromDeal(omit(block, ["type", "title"]))(
            context.contact as any
          );

          return emptyOk();
        });
        return promisedComponent(promised, {
          loading: Loader({ mode: "msgShort" }),
          resolved: function Resolved() {
            useEffect(() => {
              onChange?.({ pushStack: false });
            });
            return <></>;
          },
        });
      };
    case "httpRequest":
      return function HttpPost({ onChange, context, data }) {
        const promised = usePromise(async () => {
          const options =
            block.options &&
            evalExprStr(block.options, {
              ...data,
              data,
              ctx: context,
            });
          const query =
            block.query &&
            evalExprStr(block.query, {
              ...data,
              data,
              ctx: context,
            });
          const response = await apiCall(HTTP_POST_REQUEST_DOMAIN)(
            block.uri,
            typeof options === "object" ? options : undefined,
            typeof query === "object" ? (query as Record<string, string>) : undefined
          );

          onChange?.({
            value: response,
            path: block.path ?? "ctx.response",
            pushStack: false,
          });

          return emptyOk();
        });
        return promisedComponent(promised, {
          loading: Loader({ mode: "msgShort" }),
          resolved: function Resolved() {
            return <></>;
          },
        });
      };
    case "bitrixChat":
      return function BitrixChat({
        data,
        mobile,
        onChange,
        context,
        bitrix,
        className,
        onBack,
        ...rest
      }) {
        const [dialog, setDialog] = useState(bitrix.dialog);
        const Keyboard = Interactive({
          type: "input",
          blockName: "ctx.chatMessage",
          placeholder: i18n.t("placeholder.bitrixChat"),
          required: true,
        });
        useEffect(() => {
          bitrix.start(block.initialMessage);
          const f = () => setDialog(getWidgetState().bitrixDialog ?? bitrix.dialog);

          bitrix.onMessage(f);

          return () => void bitrix.cancelOnMessage(f);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
          <>
            <Portal id={DIALOG_BOX_PORTAL_ID}>
              <DialogBox html dialog={dialog} onChange={onChange} />
            </Portal>
            <div>
              <Keyboard
                bitrix={bitrix}
                mobile={mobile}
                data={data}
                context={context}
                className={className}
                onChange={(arg) => {
                  // console.log(arg);

                  if (!arg) return;
                  bitrix.send(arg.value as string);
                }}
              />
            </div>
            <DialogBoxFooter mode="chat" onBack={onBack} />
          </>
        );
      };
    case "promocode":
      return function Promocode({ onChange, data: _data, context }) {
        const mobile = useMobileQuery();
        const [promocode, setPromocode] = useState<string | null>(null);
        const [pull, setPull] = useState<{}>({});
        const [start, setStart] = useState(true);
        const search = new URLSearchParams(window.top?.location.search);
        const cartItemId = search.get("cartItemId") ?? "";
        const [contractDiscounted, setContractDiscounted] = useState(0);
        const [contractTotal, setContractTotal] = useState(0);
        const orderHash = useRef(context.orderHash);
        // const [total, setTotal] = useState(context.response.data.order?.payment ?? 0);

        const updatePP = async () => {
          if (!cartItemId) return err({ message: "No cart item id" });
          const {
            data: { contracts: cartContracts },
          } = await frontendAjaxCall("/web/cart", { method: "GET" });

          const item = getCartItemFromCart(cartContracts, cartItemId);
          if (!item)
            return err({
              message: "No such item in cart, " + cartItemId,
            });

          const resDoc = await frontendAjaxCall(
            "/web/calculators/integrity/save-insurance/" + item.order_hash,
            {
              method: "POST",
              headers: { "Content-Type": JSON_CONTENT_TYPE },
            }
          );

          const {
            data: { contracts },
          } = resDoc;

          window.top?.document.body.dispatchEvent(
            new CustomEvent("after-save", {
              detail: { contractId: cartItemId, contracts, cartContracts },
            })
          );

          onChange?.(
            {
              value: resDoc,
              path: "ctx.response",
              pushStack: false,
              progressScenario: false,
            },
            ...Object.entries(
              contracts as {
                [id in string]: { pp_document: string; pp_document_name: string };
              }
            ).map(([id, { pp_document, pp_document_name }]) => ({
              dialogBlockType: "bot" as const,
              type: "file" as const,
              link: pp_document,
              name: `${pp_document_name}.pdf`,
            }))
          );
          onChange?.({
            value: item.order_hash,
            path: "ctx.orderHash",
            pushStack: false,
            progressScenario: false,
          });
          const payment = resDoc.data.contracts[cartItemId].payment;
          setContractDiscounted(payment);
          setContractTotal(resDoc.data.contracts[cartItemId].full_payment);
          orderHash.current = item.order_hash;

          return ok(resDoc.data);
        };
        const promiseProductTotal = usePromise(
          async (updateTitle): Promise<Result<string | null, any>> => {
            if (promocode === null) {
              updateTitle({
                mode: "msgLong",
                title: i18n.t("preloader.ppDocument"),
              });
              const result = await updatePP();
              if (result.type === "err") return result;
              const response = result.value;
              const promocode = response.promo?.code;
              const hasPromocode = !!promocode;
              onChange?.(
                { progressScenario: false, pushStack: false },
                {
                  dialogBlockType: "bot",
                  type: "text",
                  text: i18n.t("payment.totalPrice", {
                    // discountText,
                    total: response.order?.payment ?? 0,
                    // cartTotal,
                    // promocode,
                    formatParams: { total: { locale: NUMBER_FORMAT_LOCALE } },
                  }),
                }
                // {
                //   dialogBlockType: "bot",
                //   type: "text",
                //   text: i18n.t("payment.contractPrice", {
                //     // discountText,
                //     total: response.contracts[cartItemId].payment,
                //     // cartTotal,
                //     // promocode,
                //     formatParams: { total: { locale: NUMBER_FORMAT_LOCALE } },
                //   }),
                // }
              );

              if (hasPromocode) {
                onChange?.(
                  { progressScenario: false, pushStack: false },
                  {
                    dialogBlockType: "bot",
                    type: "text",
                    text: i18n.t("promocode.alreadyHasPromocode", {
                      promocode,
                      // discountText: promocode.value,
                    }),
                  }
                );
                return ok(promocode);
              } else if (start) {
                onChange?.(
                  { progressScenario: false, pushStack: false },
                  {
                    dialogBlockType: "bot",
                    type: "text",
                    text: i18n.t("promocode.noPromocode"),
                  }
                );
              }
              return ok(null);
            }

            try {
              // apply promocode to user's cart
              updateTitle(i18n.t("preloader.promocodeApply"));
              const hash = orderHash.current;
              const productId = context.productId;
              await frontendAjaxCall(`/web/promo/add/${hash}/${productId}`, {
                method: "POST",
                body: { promocode },
                headers: { "Content-Type": JSON_CONTENT_TYPE },
              });

              updateTitle({
                mode: "msgLong",
                title: i18n.t("preloader.ppDocument"),
              });
              const res = unwrapOk(await updatePP());
              const total = res.order?.payment ?? 0;
              // setTotal(total);
              const args: any[] = [
                {
                  // value: total,
                  // path: "total",
                  // pushStack: false,
                  pushStack: false,
                  progressScenario: false,
                },
              ];
              // if (Object.keys(res.contracts).length > 1)
              args.push({
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("payment.totalPrice", {
                  // discountText,
                  total,
                  // cartTotal,
                  // promocode,
                }),
              });
              args.push({
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("promocode.success", {
                  // discountText,
                  // total,
                  // cartTotal,
                  promocode,
                }),
              });
              onChange?.(...args);
              return ok(promocode);
            } catch (e: unknown) {
              onChange?.(
                { progressScenario: false, pushStack: false },
                {
                  dialogBlockType: "bot",
                  type: "text",
                  text:
                    (e as RequestError).payload.join(", ") || i18n.t("promocode.failed"),
                }
              );
              return ok(null);
            }
          },
          [promocode, pull]
        );

        return promisedComponent(promiseProductTotal, {
          loading: Loader({ mode: "msgShort" }),
          resolved: ({ res: _promocode = null }) => {
            const onFinish: (promocode: any) => void = (promocode) => {
              setPromocode(promocode);
              onChange?.(
                {
                  path: "promocode",
                  value: promocode,
                  progressScenario: false,
                  pushStack: false,
                  // rerenderKeyboard: true,
                },
                {
                  dialogBlockType: "user",
                  type: "text",
                  text: promocode,
                }
              );
            };

            if (start && _promocode === null) {
              return (
                <Buttons
                  items={[
                    {
                      name: i18n.t("promocode.yes"),
                      onClick: () => {
                        setStart(false);
                        onChange?.(
                          {
                            pushStack: false,
                            progressScenario: false,
                          },
                          {
                            dialogBlockType: "user",
                            type: "text",
                            text: i18n.t("promocode.yes"),
                          },
                          {
                            dialogBlockType: "bot",
                            type: "text",
                            text: i18n.t("promocode.enterPromocode"),
                          }
                        );
                      },
                    },
                    {
                      name: i18n.t("promocode.no"),
                      onClick: () => {
                        setStart(false);
                        onChange?.(
                          {
                            path: "promocode",
                            value: "",
                          },
                          {
                            dialogBlockType: "user",
                            type: "text",
                            text: i18n.t("promocode.no"),
                          }
                        );
                      },
                    },
                  ]}
                />
              );
            }

            if (!start && (promocode === null || _promocode === null)) {
              return (
                <TitledInputForm
                  style={{ marginBottom: 22, marginTop: 16 }}
                  borderless
                  required
                  onFinish={onFinish}
                >
                  <Input placeholder={i18n.t("placeholder.promocode")} />
                </TitledInputForm>
              );
            }

            return (
              <Flex.Col gap={32} style={{ width: "100%" }}>
                {/* <Flex.Row gap={32} align="center" justify="end">
                  <Flex.Item
                    style={{
                      fontWeight: "bold",
                      fontSize: 32,
                      lineHeight: "32px",
                      width: "fit-content",
                      color: "rgba(255, 206, 107, 1)",
                    }}
                  >
                    {total} грн
                  </Flex.Item> */}
                {!start && (
                  <Flex.Col gap={8} align="end">
                    <Flex.Item
                      style={{
                        fontWeight: "bold",
                        fontSize: 20,
                        lineHeight: "24px",
                        width: "fit-content",
                        color: mobile ? "#453F9B" : "rgba(255, 206, 107, 1)",
                      }}
                    >
                      {contractDiscounted} грн
                    </Flex.Item>
                    <Flex.Item
                      style={{
                        position: "relative",
                        fontWeight: "bold",
                        fontSize: 28,
                        lineHeight: "28px",
                        width: "fit-content",
                        color: mobile ? "#333333" : "white",
                      }}
                    >
                      {contractTotal} грн
                      <div
                        style={{
                          position: "absolute",
                          border: "solid 2px rgba(242, 72, 34, 1)",
                          width: "calc(100% + 8px)",
                          top: "50%",
                          left: -4,
                        }}
                      />
                    </Flex.Item>
                  </Flex.Col>
                )}
                {/* </Flex.Row> */}
                <Flex.Item>
                  <Buttons
                    items={[
                      {
                        name: i18n.t("promocode.cancelPromocode"),
                        onClick: async () => {
                          const hash = orderHash.current;
                          const productId = context.productId;
                          await frontendAjaxCall(
                            `/web/promo/delete/${hash}/${productId}`,
                            {
                              method: "POST",
                              body: { promocode: _promocode },
                              headers: { "Content-Type": JSON_CONTENT_TYPE },
                            }
                          );
                          // await updatePP();
                          onChange?.(
                            { progressScenario: false, pushStack: false },
                            {
                              dialogBlockType: "user",
                              type: "text",
                              text: i18n.t("promocode.cancelPromocode"),
                            },
                            {
                              dialogBlockType: "bot",
                              type: "text",
                              text: i18n.t("promocode.promocodeCanceled", { promocode }),
                            }
                          );
                          setPromocode(null);
                          setStart(true);
                          setPull({});
                        },
                      },
                      {
                        name: i18n.t("promocode.continue"),
                        onClick: () =>
                          onChange?.(
                            {},
                            {
                              dialogBlockType: "user",
                              type: "text",
                              text: i18n.t("promocode.continue"),
                            }
                          ),
                      },
                    ]}
                  />
                </Flex.Item>
              </Flex.Col>
            );
          },
        });
      };
    case "promocode2":
      return function Branch({ onChange }) {
        useEffect(() => {
          onChange?.({ pushStack: false, next: PromocodeBranch as unknown as string });
        });
        return <></>;
      };
    case "promocodeDisplay":
      return function Branch({ data, context, onChange }) {
        const mobile = useMobileQuery();
        const orderHash = context.orderHash;
        const promocode =
          block.promocode &&
          evalExprStr(block.promocode, {
            ...data,
            data,
            ctx: context,
          });
        const contractTotal =
          block.contractTotal &&
          evalExprStr(block.contractTotal, {
            ...data,
            data,
            ctx: context,
          });
        const contractDiscounted =
          block.contractDiscounted &&
          evalExprStr(block.contractDiscounted, {
            ...data,
            data,
            ctx: context,
          });

        return (
          <Flex.Col gap={32} style={{ width: "100%" }}>
            {/* <Flex.Row gap={32} align="center" justify="end">
                <Flex.Item
                  style={{
                    fontWeight: "bold",
                    fontSize: 32,
                    lineHeight: "32px",
                    width: "fit-content",
                    color: "rgba(255, 206, 107, 1)",
                  }}
                >
                  {total} грн
                </Flex.Item> */}
            <Flex.Col gap={8} align="end">
              <Flex.Item
                style={{
                  fontWeight: "bold",
                  fontSize: 20,
                  lineHeight: "24px",
                  width: "fit-content",
                  color: mobile ? "#453F9B" : "rgba(255, 206, 107, 1)",
                }}
              >
                {String(contractDiscounted)} грн
              </Flex.Item>
              <Flex.Item
                style={{
                  position: "relative",
                  fontWeight: "bold",
                  fontSize: 28,
                  lineHeight: "28px",
                  width: "fit-content",
                  color: mobile ? "#333333" : "white",
                }}
              >
                {String(contractTotal)} грн
                <div
                  style={{
                    position: "absolute",
                    border: "solid 2px rgba(242, 72, 34, 1)",
                    width: "calc(100% + 8px)",
                    top: "50%",
                    left: -4,
                  }}
                />
              </Flex.Item>
            </Flex.Col>
            <Flex.Item>
              <Buttons
                items={[
                  {
                    name: i18n.t("promocode.cancelPromocode"),
                    onClick: async () => {
                      const productId = context.productId;
                      await frontendAjaxCall(
                        `/web/promo/delete/${orderHash}/${productId}`,
                        {
                          method: "POST",
                          body: { promocode },
                          headers: { "Content-Type": JSON_CONTENT_TYPE },
                        }
                      );

                      onChange?.(
                        {
                          value: false,
                          path: block.path,
                          pushStack: false,
                        },
                        {
                          dialogBlockType: "user",
                          type: "text",
                          text: i18n.t("promocode.cancelPromocode"),
                        },
                        {
                          dialogBlockType: "bot",
                          type: "text",
                          text: i18n.t("promocode.promocodeCanceled", { promocode }),
                        }
                      );
                    },
                  },
                  {
                    name: i18n.t("promocode.continue"),
                    onClick: () => {
                      return onChange?.(
                        {
                          value: true,
                          path: block.path,
                          pushStack: false,
                        },
                        {
                          dialogBlockType: "user",
                          type: "text",
                          text: i18n.t("promocode.continue"),
                        }
                      );
                    },
                  },
                ]}
              />
            </Flex.Item>
          </Flex.Col>
        );
      };
    case "applyPromocode":
      return function Confirm({ data, context, onChange }) {
        const promocode = block.promocode
          ? evalExprStr(block.promocode, {
              ...data,
              data,
              ctx: context,
            }) ?? data
          : data;

        const promiseProductTotal = usePromise(async () => {
          const hash = context.orderHash;
          const productId = context.productId;

          try {
            await frontendAjaxCall(`/web/promo/add/${hash}/${productId}`, {
              method: "POST",
              body: { promocode },
              headers: { "Content-Type": JSON_CONTENT_TYPE },
            });

            onChange?.({
              value: true,
              path: block.path,
              pushStack: false,
            });
            return emptyOk();
          } catch (e: any) {
            const text =
              (e as RequestError).payload.join(", ") || i18n.t("promocode.failed");
            onChange?.(
              {
                value: false,
                path: block.path,
                pushStack: false,
              },
              { dialogBlockType: "bot", type: "text", text }
            );
            return emptyOk();
          }
        });

        return promisedComponent(promiseProductTotal, {
          loading: Loader({
            mode: "msgLong",
            title: i18n.t("preloader.promocodeApply"),
          }),
          resolved: () => <></>,
        });
      };
    case "calculatePrice":
      return function Confirm({ data, context, onChange }) {
        const product = block.product
          ? evalExprStr(block.product, {
              ...data,
              data,
              ctx: context,
            }) ?? context.productId
          : context.productId;
        const body = block.data
          ? evalExprStr(block.data, {
              ...data,
              data,
              ctx: context,
            }) ?? data
          : data;

        const promiseProductTotal = usePromise(async () => {
          const {
            data: { response: responsePayment },
          } = await frontendAjaxCall(
            "/web/calculators/integrity/calculate-insurance/" + product,
            {
              method: "POST",
              body,
              headers: {
                "Content-Type": JSON_CONTENT_TYPE,
              },
            }
          );

          onChange?.({
            value: responsePayment?.result?.payment,
            path: "total",
            pushStack: false,
            progressScenario: false,
          });

          onChange?.({
            value: responsePayment?.result?.tariff_id,
            path: "tariff_id",
            pushStack: false,
            progressScenario: false,
          });

          onChange?.({
            value: responsePayment,
            path: block.path ?? "ctx.responsePayment",
            pushStack: false,
          });

          return emptyOk();
        });

        return promisedComponent(promiseProductTotal, {
          loading: Loader({
            mode: "msgLong",
            title: i18n.t("preloader.calculatePrice"),
          }),
          resolved: () => <></>,
        });
      };
    case "authorize":
      return function Authorize({ data, onChange, context, ...rest }) {
        const lang = useLanguage();
        const update = async (delta: Partial<UserProfile>) => {
          await frontendAjaxCall("/profile/person/save", { body: delta });
          onChange?.({
            pushStack: false,
            progressScenario: false,
            rerenderKeyboard: true,
          });
        };
        const save = (authed: UserProfile) => {
          if (!authed.phone && !block.noPrompts) throw new Error("noPhone");
          if (authed.phone) authed.phone = authed.phone.replace(/[^\d]/, "");

          onChange?.({
            path: "ctx.authed",
            value: authed,
            pushStack: false,
          });

          emitEvent("auth-success", { userId: authed.id }, window.top?.document.body);
        };
        const promiseUserInfo = usePromise(async () => {
          try {
            if (context.authed) {
              if (!context.authed.phone && !block.noPrompts) throw new Error("noPhone");
              context.authed.phone = context.authed.phone.replace(/[^\d]/, "");
              onChange?.({ pushStack: false });

              return emptyOk();
            }

            const user = await getAuthedUser();
            save(user.data);
            return emptyOk();
          } catch ({ message }) {
            if (block.noPrompts) {
              onChange?.({ pushStack: false });
              return emptyOk();
            }
            if (message === "noPhone") {
              onChange?.(
                { progressScenario: false, pushStack: false },
                {
                  dialogBlockType: "bot",
                  type: "text",
                  text: i18n.t("authorize.noPhone"),
                }
              );
              return err({ message });
            }
            onChange?.(
              { progressScenario: false, pushStack: false },
              {
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("authorize.noAuth", {
                  privacyPolicy: [FRONTEND_DOMAIN, lang, PRIVACY_POLICY_URL].join("/"),
                }),
              },
              {
                dialogBlockType: "bot",
                type: "text",
                text: i18n.t("authorize.privacyPolicyText"),
              }
            );
            return err({ message });
          }
        });

        return promisedComponent<(typeof promiseUserInfo & { state: "resolved" })["res"]>(
          promiseUserInfo,
          {
            loading: Loader({
              mode: "msgShort",
              title: i18n.t("preloader.authorize"),
            }),
            resolved: () => {
              return <></>;
            },
            rejected: ({ err }) => {
              const message = (err as Error).message;
              if (message === "noPhone")
                return (
                  <Auth
                    {...rest}
                    onChange={onChange}
                    data={data}
                    context={context}
                    onAuth={async (_, phone) => {
                      await update({ phone });
                    }}
                  />
                );

              return (
                <Auth
                  {...rest}
                  authServices
                  onChange={onChange}
                  data={data}
                  context={context}
                  onAuth={async () => {
                    try {
                      const { data: authed } = await getAuthedUser();
                      assert(!Array.isArray(authed));
                      save(authed);
                    } catch {
                      onChange?.({
                        pushStack: false,
                        progressScenario: false,
                        rerenderKeyboard: true,
                      });
                    }
                  }}
                />
              );
            },
          }
        );
      };
    // TODO: refactor form
    case "form":
      return function Form({ onChange, context, data }) {
        const [form] = useForm();
        const [buttonValue, setButtonValue] = useState<
          | {
              text: string;
              value: unknown;
              next?: string;
              name: string;
              path: string;
            }
          | undefined
        >();
        const evalContext = { ...data, data, ctx: context };
        const inputs = block.inputs.filter((item) => {
          if (item.hidden === undefined || item.hidden === null) return true;
          if (typeof item.hidden === "boolean") return !item.hidden;
          return !evalExprStr(item.hidden, evalContext);
        });

        return (
          <AntForm
            scrollToFirstError
            style={{ width: "100%" }}
            form={form}
            onFinish={(fields) => {
              const value: any = {};
              for (const input of inputs) {
                const val = fields[input.blockName];
                const pathName = `${block.blockName}.${input.blockName}`;

                if (val && typeof val === "object") {
                  const { name, value: _value } = val as {
                    name: string;
                    value: unknown;
                  };
                  if (input.type === "checkbox")
                    onChange?.({
                      name,
                      path: pathName,
                      block: {
                        type: "button",
                        blockName: pathName,
                        items: [
                          { name: "Да", value: "true" },
                          { name: "Нет", value: "false" },
                        ],
                      },
                      progressScenario: false,
                      pushStack: false,
                    });
                  else
                    onChange?.({
                      name: name ?? _value,
                      path: pathName,
                      block: {
                        ...input,
                        blockName: pathName,
                      },
                      progressScenario: false,
                      pushStack: false,
                    });
                  value[input.blockName] = _value;
                } else {
                  onChange?.({
                    name: val,
                    path: pathName,
                    block: {
                      ...input,
                      blockName: pathName,
                    } as InteractiveBlock,
                    progressScenario: false,
                    pushStack: false,
                  });
                  value[input.blockName] = val;
                }
              }
              const text = inputs
                .flatMap(({ blockName, title, fieldName, ...item }) => {
                  if (!fields[blockName]) return [];

                  const answer =
                    typeof fields[blockName] === "object"
                      ? fields[blockName].name ?? fields[blockName].value
                      : fields[blockName];
                  return [`${fieldName ?? title}: ${answer}`];
                })
                .join("\n");

              onChange?.(
                { value, path: block.blockName },
                {
                  dialogBlockType: "user",
                  type: "text",
                  text,
                }
              );

              if (buttonValue) {
                const { text, ...x } = buttonValue;
                onChange?.(x, { dialogBlockType: "user", type: "text", text });
              }
            }}
          >
            <Portal id={DIALOG_BOX_PORTAL_ID}>
              <div
                style={{
                  background: "white",
                  padding: "12px 24px 30px 16px",
                  borderRadius: "1px 12px 12px 12px",
                }}
              >
                {block.allowDiia && (
                  <Button type="text">{i18n.t("form.importFromDiia")}</Button>
                )}
                {block.allowDiia && <Divider style={{ margin: "16px 0px" }} />}
                <div style={{ display: "flex", flexDirection: "column", rowGap: 18 }}>
                  {inputs.map((item) => {
                    const rules = [
                      {
                        validator: async (_: any, value: string) => {
                          if (!(!("required" in item) || item.required)) return;
                          if (item.type !== "checkbox" && !value)
                            throw new Error(i18n.t("input.required"));
                          else if (value === undefined)
                            throw new Error(i18n.t("input.required"));
                        },
                      },
                      "validation" in item && item.validation
                        ? (item.validation as unknown as Rule[])
                        : [],
                      item.type === "phone"
                        ? ([
                            {
                              validator: async (_: any, value: string) => {
                                value = value.startsWith("+") ? value : "+" + value;
                                if (isPossiblePhoneNumber(value)) return;
                                else
                                  throw new Error(i18n.t("input.impossiblePhoneNumber"));
                              },
                            },
                          ] as unknown as Rule[])
                        : [],
                    ]
                      .flat()
                      .map((rule) =>
                        "pattern" in rule && rule.pattern
                          ? ({
                              ...rule,
                              pattern: new RegExp(
                                "^" + new RegExp(rule.pattern).source + "$"
                              ),
                            } as Rule)
                          : rule
                      );

                    let initialValue: any = item.defaultValue
                      ? evalExprStr(item.defaultValue, evalContext)
                      : item.resetOnEnter
                      ? undefined
                      : item.blockName && evalExprStr(item.blockName, evalContext);

                    if (item.type === "phone" && initialValue !== undefined)
                      initialValue =
                        typeof initialValue === "string" && initialValue.startsWith("+")
                          ? initialValue
                          : "+" + initialValue;
                    if (item.type === "date" && initialValue !== undefined)
                      initialValue = new Date(initialValue as string | number);
                    if (item.type === "date" && initialValue !== undefined)
                      initialValue =
                        moment.isDate(initialValue) &&
                        !Number.isNaN(initialValue.getTime())
                          ? initialValue
                          : undefined;
                    if (item.type === "date" && initialValue !== undefined)
                      initialValue = ((item.disabledDate &&
                        evalExprStr(item.disabledDate, {
                          ...evalContext,
                          selected: initialValue,
                        })) as boolean)
                        ? undefined
                        : initialValue;
                    if (item.type === "date" && initialValue !== undefined)
                      initialValue = {
                        value: moment.utc(initialValue).format("YYYY-MM-DD"),
                        name: moment.utc(initialValue).format(DATE_FORMAT),
                      };
                    if (item.type === "slider" && initialValue !== undefined)
                      initialValue = +initialValue;
                    if (item.type === "dropdown" && initialValue !== undefined)
                      initialValue = { value: initialValue };
                    if (item.type === "checkbox")
                      initialValue = { value: initialValue ?? false };

                    let hidden;
                    if (item.hidden === undefined || item.hidden === null) hidden = false;
                    else if (typeof item.hidden === "boolean") hidden = item.hidden;
                    else hidden = evalExprStr(item.hidden, evalContext);
                    if (typeof hidden !== "boolean") hidden = !!hidden;

                    if (hidden) return null;

                    return (
                      <div
                        key={item.blockName}
                        className={styles.formInput}
                        style={{ position: hidden ? "absolute" : undefined }}
                      >
                        {!hidden && item.type !== "checkbox" && item.title && (
                          <div style={{ paddingBottom: 12 }}>{item.title}</div>
                        )}
                        <AntForm.Item
                          name={item.blockName}
                          rules={item.type !== "button" ? rules : []}
                          style={{ margin: 0 }}
                          initialValue={initialValue}
                          hidden={hidden}
                          validateFirst
                          getValueFromEvent={
                            item.type === "checkbox" ? (e) => e : undefined
                          }
                          getValueProps={
                            item.type === "checkbox"
                              ? (value) => ({ value: value?.value ?? value })
                              : undefined
                          }
                        >
                          <ScenarioFormItem
                            context={evalContext}
                            item={item}
                            className={styles.input}
                            onButtonClick={(value) => {
                              const path = `${block.blockName}.${item.blockName}`;
                              setButtonValue({ ...value, path });
                              form.submit();
                            }}
                          />
                        </AntForm.Item>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Portal>
            <AntForm.Item noStyle>
              <Buttons
                style={{ paddingTop: 32 }}
                items={[
                  {
                    name: block.submitText ?? i18n.t("input.submitForm"),
                    onClick: () => form.validateFields().then(form.submit),
                  },
                ]}
              />
            </AntForm.Item>
          </AntForm>
        );
      };
    case "signContract":
      return function Sign({ context, onChange }) {
        // const { authed } = context;
        // const { id: userId } = (authed as Record<string, string>) ?? {
        //   id: 170,
        // };

        const [state, setState] = useState("send");
        const [otp, setOtp] = useState<string>();
        const { state: promised, execute } = usePromiseExt(
          async (updateTitle) => {
            if (state === "signing" && otp) {
              updateTitle(i18n.t("preloader.signContract"));

              try {
                const resDoc = await frontendAjaxCall(
                  "/web/calculators/integrity/buy-insurance/" + context.orderHash,
                  {
                    method: "POST",
                    body: {
                      otp,
                      writing_method: 1,
                    },
                    headers: { "Content-Type": JSON_CONTENT_TYPE },
                  }
                );

                const { data: responseDoc } = resDoc;

                const docs = responseDoc.contracts as {
                  [id in string]: { documents: string[] };
                };

                onChange?.(
                  { pushStack: false },
                  {
                    dialogBlockType: "bot",
                    type: "text",
                    text: i18n.t("signContract.signSuccessCongrats"),
                  },
                  ...Object.entries(docs).flatMap(([id, { documents }]) =>
                    documents.map((link, i) => ({
                      dialogBlockType: "bot" as const,
                      type: "file" as const,
                      link,
                      name: i18n.t("documentName." + i, { id }),
                    }))
                  )
                );
              } catch (e: any) {
                const msg = e.payload;
                onChange?.(
                  { progressScenario: false, pushStack: false },
                  {
                    dialogBlockType: "bot",
                    type: "text",
                    text: typeof msg === "string" ? msg : msg[0],
                  }
                );
              }
            } else if (state === "send") {
              setState("signing");
              updateTitle(i18n.t("preloader.sendCode"));

              await frontendAjaxCall("/web/user/sms/otp/resend");

              onChange?.({
                progressScenario: false,
                pushStack: false,
              });
            }
            return emptyOk();
          },
          [otp],
          { doInitialFetch: true }
        );

        return promisedComponent(promised, {
          resolved: () => (
            <div style={{ width: "100%" }}>
              <Buttons
                items={[
                  {
                    name: i18n.t("sendNewCodeSMS"),
                    onClick: async () => {
                      setState("send");
                      execute();
                      onChange?.(
                        { progressScenario: false, pushStack: false },
                        {
                          dialogBlockType: "user",
                          type: "text",
                          text: i18n.t("sendNewCodeSMS"),
                        }
                      );
                    },
                  },
                ]}
              />
              <TitledInputForm
                style={{ marginBottom: 22, marginTop: 16 }}
                rules={[
                  {
                    pattern: /\d+/,
                    message: i18n.t("input.digitsOnly"),
                  },
                ]}
                required
                borderless
                onFinish={(otp) => {
                  setOtp(otp);
                  onChange?.(
                    { progressScenario: false, pushStack: false },
                    {
                      dialogBlockType: "user",
                      type: "text",
                      text: otp,
                    }
                  );
                }}
              >
                <Input placeholder={i18n.t("placeholder.signContract")} />
              </TitledInputForm>
            </div>
          ),
        });
      };
    case "validate":
      const {
        answersToValidate = [],
        text,
        yesButtonText = i18n.t("validate.defaultYesButtonText") as string,
      } = block;
      return function Validate({ data, context, onChange, ...rest }) {
        const ctx = { ...data, ctx: context };

        useEffect(() => {
          if (text) {
            if (context.validateState === "change") {
            } else if (context.validateState === "edit") {
            } else {
              onChange?.(
                { progressScenario: false, pushStack: false },
                { type: "text", text, dialogBlockType: "bot" }
              );
            }
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        if (context.validateState === "change")
          return (
            <Buttons
              items={[
                ...answersToValidate.map(({ title, blockName: path }) => ({
                  name: title,
                  onClick: () => {
                    onChange?.({
                      value: evalAccessStr(path, ctx).join("."),
                      path: "ctx.editPath",
                      pushStack: false,
                      progressScenario: false,
                    });
                    onChange?.(
                      {
                        value: "edit",
                        path: "ctx.validateState",
                        progressScenario: false,
                        rerenderKeyboard: true,
                      },

                      {
                        dialogBlockType: "user",
                        type: "text",
                        text: title,
                      },
                      {
                        dialogBlockType: "bot",
                        type: "text",
                        text: i18n.t("validate.newInput"),
                      }
                    );
                  },
                })),
                {
                  name: i18n.t("validate.submitChange"),
                  onClick: () =>
                    onChange?.(
                      {
                        value: "verified",
                        path: "ctx.validateState",
                        pushStack: false,
                      },
                      {
                        dialogBlockType: "user",
                        type: "text",
                        text: i18n.t("validate.submitChange"),
                      }
                    ),
                },
              ]}
            />
          );

        if (context.validateState === "edit") {
          const Keyboard = Interactive(
            (context.answers as any)[context.editPath as any].block
          );
          return (
            <Keyboard
              data={data}
              context={context}
              onChange={(...args) => {
                const [value, ...rest] = args;
                if (!value) {
                  onChange?.();
                  return;
                }

                onChange?.(
                  { ...value, progressScenario: false, pushStack: false },
                  ...rest
                );
                if (value.progressScenario ?? true)
                  onChange?.(
                    {
                      value: "change",
                      path: "ctx.validateState",
                      progressScenario: false,
                      rerenderKeyboard: true,
                    },
                    ...(text
                      ? [
                          {
                            type: "text" as const,
                            text,
                            dialogBlockType: "bot" as const,
                          },
                          {
                            dialogBlockType: "bot" as const,
                            type: "text" as const,
                            text: i18n.t("validate.requestChange"),
                          },
                        ]
                      : [
                          {
                            dialogBlockType: "bot" as const,
                            type: "text" as const,
                            text: i18n.t("validate.requestChange"),
                          },
                        ])
                  );
              }}
              {...rest}
            />
          );
        }

        return (
          <Buttons
            items={[
              {
                name: yesButtonText,
                onClick: () =>
                  onChange?.(
                    {
                      value: "verified",
                      path: "ctx.validateState",
                    },
                    {
                      dialogBlockType: "user",
                      type: "text",
                      text: yesButtonText,
                    }
                  ),
              },
              {
                name: i18n.t("validate.changeRequest"),
                onClick: () =>
                  onChange?.(
                    {
                      value: "change",
                      path: "ctx.validateState",
                      progressScenario: false,
                      rerenderKeyboard: true,
                    },

                    {
                      dialogBlockType: "user",
                      type: "text",
                      text: i18n.t("validate.changeRequest"),
                    },
                    {
                      dialogBlockType: "bot",
                      type: "text",
                      text: i18n.t("validate.requestChange"),
                    }
                  ),
              },
            ]}
          />
        );
      };
    case "searchCar":
      return function SearchCar({ onChange, data, context }) {
        const number = evalExprStr(block.regNumber, {
          ...data,
          data,
          ctx: context,
        }) as string;
        const promised = usePromise(async () => {
          try {
            const { data } = await frontendAjaxCall("/api/v1/integrity/search-car", {
              body: { number },
              method: "POST",
            });

            if (Array.isArray(data.car)) return ok(null);

            return ok(data);
          } catch (e) {
            return ok(null);
          }
        });
        return promisedComponent(promised, {
          loading: Loader({
            mode: "msgShort",
            title: i18n.t("preloader.searchCar"),
          }),
          resolved: function Resolved({ res }) {
            useEffect(() => {
              onChange?.({ value: res, path: block.path, pushStack: false });
            });
            return <></>;
          },
        });
      };
    case "clearCallStack":
      return function F({ onChange }) {
        useEffect(() => {
          const x = setTimeout(() => {
            onChange?.({ pushStack: false, clearCallStack: true });
          });
          return () => clearTimeout(x);
        });
        return <></>;
      };
    case "emitEvent":
      return function EmitEvent({ onChange, context, data }) {
        useEffect(() => {
          const _data = evalExprStr(block.data, {
            ...data,
            data,
            ctx: context,
          }) as string;

          // console.log(
          //   "emitting event",
          //   block,
          //   block.name,
          //   _data,
          //   window.top?.document.body
          // );

          emitEvent(block.name, _data, window.top?.document.body);
          // console.log("emitted, continuing scenario");
          onChange?.({ pushStack: false });
        });
        return <></>;
      };
    default:
      return function Noop({ onChange }) {
        useEffect(() => {
          const x = setTimeout(() => {
            onChange?.({ pushStack: false });
          });
          return () => clearTimeout(x);
        });
        return <></>;
      };
  }
};

const InteractiveHidden = (
  block: (InteractiveBlock | DialogContent) & { title?: string }
): InteractiveBlockComponent => {
  const Renderer = Interactive(block);
  return function Noop(props) {
    const { onChange, data, context } = props;
    const hidden = useMemo(() => {
      const evalContext = { ...data, data, ctx: context };
      return "hidden" in block && block.hidden
        ? evalExprStr(block.hidden, evalContext)
        : false;
    }, [data, context]);

    useEffect(() => {
      const x = setTimeout(() => {
        if (hidden) onChange?.({ pushStack: false });
      });
      return () => clearTimeout(x);
    }, [hidden, onChange]);
    if (hidden) return <></>;

    return <Renderer {...props} />;
  };
};

export default InteractiveHidden;
